import React from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Link, useStaticQuery, graphql } from "gatsby"
import Helmet from "react-helmet"

import Layout from "../components/layout/layout.js"
import PageSection from '../components/pageSection/pageSection.js'
import Seo from "../components/seo"
import H1 from "../components/h1/h1.js"
import SharedFooter from "../components/sharedFooter/sharedFooter.js"
import { ConfettiHero } from "../components/confettiHero/confettiHero.js"
import { BlogNewsSignUp } from "../components/blogNewsSignUp/blogNewsSignUp.js"

import { getQueryParamValueByName } from "../utils/query_params.js"
import { getDevice } from "../utils/getDevice.js"

import styles from "./blog.module.scss"

const BlogPostTemplate = props => {

  const data = useStaticQuery(graphql`
    query {
      gang: file(relativePath: { eq: "blog/blog-hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 541
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      },
      lifeWithTill: file(relativePath: { eq: "blog/life-with-till/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      girlsShopping: file(relativePath: { eq: "blog/kids-and-spending/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      families: file(relativePath: { eq: "blog/till-was-built-for-families/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      debitCredit: file(relativePath: { eq: "blog/debit-vs-credit/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      kidCleaning: file(relativePath: { eq: "blog/a-yearning-for-earning/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      girlOnSkateboard: file(relativePath: { eq: "blog/help-your-kids-find-their-financial-footing/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      dadKidCouch: file(relativePath: { eq: "blog/helping-your-kids-get-money-right/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 544
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      motherDaughterSweetShop: file(relativePath: { eq: "blog/can-i-have-this/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      allowanceHero: file(relativePath: { eq: "blog/allowance-what-works-for-your-family/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      eyesOnThePrizeHero: file(relativePath: { eq: "blog/keep-your-eyes-on-the-prize/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      growthMindsetHero: file(relativePath: { eq: "blog/embracing-the-growth-mindset/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      developYourMBSHero: file(relativePath: { eq: "blog/how-to-easily-develop-your-teens-money-and-budgeting-skills/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      sevenSignsHero: file(relativePath: { eq: "blog/seven-signs-your-kid-is-ready-for-a-debit-card/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      teachingTeensHero: file(relativePath: { eq: "blog/teaching-teens-the-value-of-money/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      sixLifeLessonsHero: file(relativePath: { eq: "blog/six-life-lessons/hero.jpg" }) {
        childImageSharp {
          gatsbyImageData (
            width: 1200
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
  `)
  
  const { pageContext } = props
  const {
    featuredPost,
    relatedPosts
  } = pageContext

  const root = "https://tillfinancial.io";
  const efcode = getQueryParamValueByName("campaignCode") ? getQueryParamValueByName("campaignCode") : undefined;
  
  const [device, setDevice] = React.useState("");

  React.useEffect(() => {
    const d = getDevice();
    setDevice(d);
  }, []);

  return (
      <Layout useWhiteHeaderBg={false}>
        <Seo 
          title="Till Blog | Kid's Money Management Advice " 
          description="Based on collaboration and personalized guidance, Till helps extend your reach and inspire your kids." 
        />
        <Helmet>
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content="@tillfinancial" />
          <meta name="twitter:creator" content="@tillfinancial" />
          <meta property="fb:app_id" content="167885590822992" />
          <meta property="og:title" content="Till Blog" />
          <meta property="og:image" content={`${root}${getImage(data.gang).images.fallback.src}`} />
          <meta property="og:image:width" content="541" />
          <meta property="og:image:height" content="380" />
          <meta property="og:url" content={`${root}/blog`} />
        </Helmet>
        <ConfettiHero
          color="green"
          HeroImage={() =>
            <div className={styles.heroImageWrapper}>
              <GatsbyImage
                image={getImage(data.gang)}
                alt="Two smiling teenagers looking at a phone showing the Till Financial app"
                width={541}
              />
            </div>
          }
        >
        <H1 className={styles.h1}>Till Blog</H1>
        <h2 className={styles.h2}>Learn how to help your kids to develop money habits that prepare them for the real world with Till</h2>
      </ConfettiHero>
      {/*News Letter*/}
        
        <PageSection>
            {featuredPost[0] && 
              <div className={styles.featuredPostWrapper}>
                <div className={styles.featuredBlogPost}>
                  <div key={`${featuredPost[0].path}${0}`}>
                    <Link to={`/blog${featuredPost[0].path}`} className={styles.featuredPostImageWrapper}>
                      <GatsbyImage
                        image={getImage(data[featuredPost[0].featureImage])}
                        alt={featuredPost[0].featureImageAlt}
                      />
                    </Link>
                    <div className={styles.featuredPostCopyWrapper}>
                      <h2><Link to={`/blog${featuredPost[0].path}`}>{featuredPost[0].title}</Link></h2>
                      <p>{featuredPost[0].intro ? `${featuredPost[0].intro.substring(0,222)}...` : "..."}</p>
                      <Link to={`/blog${featuredPost[0].path}`} className={styles.buttonLink}>Read More</Link>
                    </div>
                  </div>
                </div>
              </div>
            }
            <BlogNewsSignUp align="true" />
        </PageSection>
        <PageSection>
        <div className={styles.blogPostsWrapper}>
          {relatedPosts.map((post, i) =>{
            if(post.featured === true){return false;}  
            return (<div key={`${post.path}${i}`}>
                      {post.featureImage ? 
                        <Link to={`/blog${post.path}`} className={styles.blogPostsImageWrapper}>
                          <GatsbyImage
                            image={getImage(data[post.featureImage])}
                            alt={post.featureImageAlt}
                          />
                        </Link>
                        : ""}
                        <h2><Link to={`/blog${post.path}`}>{post.title}</Link></h2>
                        <p>{post.intro ? `${post.intro.substring(0,222)}...` : "..."}</p>
                        <Link to={`/blog${post.path}`} className={styles.readMore}>Read More</Link>
                    </div>)}
                )}
        </div>
        </PageSection>
        <PageSection backgroundColor="#eff3f9">
          <SharedFooter efcode={efcode} device={device} />
        </PageSection>
      </Layout>
      
  )
}

export default BlogPostTemplate
